import { useMemo } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  collectControllerCreateReceiver,
  collectControllerListReceivers,
  collectControllerReissueReceiverToken,
  collectControllerRevokeReceiver,
} from '@dvvb/sdk';

import {
  Badge,
  Button,
  Card,
  Icon,
  TextInput,
  useToast,
} from '../../components/primitives';
import { ApiCallError, unwrap } from '../../lib/envelope';
import { queryClient } from '../../lib/queryClient';

interface ReceiverSummary {
  id: string;
  deviceName: string | null;
  tokenPrefix: string;
  token: string | null;
  status: 'active' | 'revoked';
  lastUsedAt: string | null;
  createdAt: string;
  revokedAt: string | null;
  agentVersion: string | null;
}

interface ReceiverListResponse {
  items: ReceiverSummary[];
}

interface ReceiverTokenResponse {
  receiver: ReceiverSummary;
  token: string;
}

interface ReceiverStatusResponse {
  receiver: ReceiverSummary;
}

function useReceiverList() {
  return useQuery({
    queryKey: ['collect', 'receivers'],
    queryFn: async () => {
      const r = await collectControllerListReceivers();
      return unwrap<ReceiverListResponse>(r);
    },
    refetchOnMount: 'always',
    staleTime: 30_000,
  });
}

function receiverSortValue(receiver: ReceiverSummary) {
  const t = new Date(receiver.createdAt).getTime();
  return Number.isNaN(t) ? 0 : t;
}

function formatTime(iso: string) {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

export function ReceiverTokenPanel() {
  const toast = useToast();
  const receiversQ = useReceiverList();

  const createMu = useMutation({
    mutationFn: async () => {
      const r = await collectControllerCreateReceiver({
        body: { deviceName: '윈도우 수신기' } as never,
      });
      return unwrap<ReceiverTokenResponse>(r);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['collect', 'receivers'] });
      toast.success('수신기 토큰을 발급했습니다.');
    },
    onError: (e) => {
      const err = e as ApiCallError;
      toast.error(err.message ?? '수신기 토큰 발급 실패');
    },
  });

  const reissueMu = useMutation({
    mutationFn: async (receiverId: string) => {
      const r = await collectControllerReissueReceiverToken({
        path: { receiverId },
        body: {} as never,
      });
      return unwrap<ReceiverTokenResponse>(r);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['collect', 'receivers'] });
      toast.success('수신기 토큰을 재발급했습니다.');
    },
    onError: (e) => {
      const err = e as ApiCallError;
      toast.error(err.message ?? '수신기 토큰 재발급 실패');
    },
  });

  const revokeMu = useMutation({
    mutationFn: async (receiverId: string) => {
      const r = await collectControllerRevokeReceiver({
        path: { receiverId },
        body: {} as never,
      });
      return unwrap<ReceiverStatusResponse>(r);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['collect', 'receivers'] });
      toast.success('수신기 토큰을 해지했습니다.');
    },
    onError: (e) => {
      const err = e as ApiCallError;
      toast.error(err.message ?? '수신기 토큰 해지 실패');
    },
  });

  const copyToken = async (token: string | null) => {
    if (!token) return;
    try {
      await navigator.clipboard.writeText(token);
      toast.success('토큰을 복사했습니다.');
    } catch {
      toast.error('토큰 복사 실패');
    }
  };

  const receivers = receiversQ.data?.items ?? [];
  const currentReceiver = useMemo(() => {
    return [...receivers]
      .sort((a, b) => receiverSortValue(b) - receiverSortValue(a))
      .find((receiver) => receiver.status === 'active') ?? null;
  }, [receivers]);
  const currentToken = currentReceiver?.token ?? null;
  const tokenPlaceholder = currentReceiver
    ? '재발급하면 새 토큰이 표시됩니다'
    : '토큰을 발급하세요';
  const busy = createMu.isPending || reissueMu.isPending || revokeMu.isPending;
  const handleIssueOrReissue = () => {
    if (currentReceiver) {
      reissueMu.mutate(currentReceiver.id);
      return;
    }
    createMu.mutate();
  };

  return (
    <Card style={{ padding: 20 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 12,
          alignItems: 'flex-start',
          marginBottom: 14,
        }}
      >
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--fg-1)' }}>
            수신기 토큰
          </div>
          <div style={{ fontSize: 12, color: 'var(--fg-3)', marginTop: 4 }}>
            윈도우 수신기에 붙여넣는 연결 토큰
          </div>
        </div>
        <Badge variant={currentReceiver ? 'success' : 'default'}>
          {currentReceiver ? '발급됨' : '미발급'}
        </Badge>
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 12,
          alignItems: 'center',
          marginBottom: 6,
          fontSize: 12,
          color: 'var(--fg-3)',
        }}
      >
        <span style={{ fontWeight: 700, color: 'var(--fg-2)' }}>토큰</span>
        {currentReceiver ? (
          <span>
            마지막 사용{' '}
            {currentReceiver.lastUsedAt ? formatTime(currentReceiver.lastUsedAt) : '-'}
          </span>
        ) : null}
      </div>

      <div
        style={{
          display: 'flex',
          gap: 8,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <div style={{ flex: '1 1 320px', minWidth: 220 }}>
          <TextInput
            value={currentToken ?? ''}
            onChange={() => undefined}
            placeholder={tokenPlaceholder}
            readOnly
            style={{
              fontFamily:
                'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
              fontSize: 12,
            }}
          />
        </div>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={busy || !currentToken}
          onClick={() => copyToken(currentToken)}
        >
          <Icon name="copy" size={14} /> 복사
        </Button>
        <Button
          type="button"
          size="sm"
          variant="strong"
          disabled={busy}
          onClick={handleIssueOrReissue}
        >
          <Icon name="refresh" size={14} /> {currentReceiver ? '재발급' : '발급'}
        </Button>
        {currentReceiver ? (
          <Button
            type="button"
            size="sm"
            variant="danger"
            disabled={busy}
            onClick={() => revokeMu.mutate(currentReceiver.id)}
          >
            해지
          </Button>
        ) : null}
      </div>

      {receiversQ.isLoading ? (
        <div style={{ fontSize: 13, color: 'var(--fg-3)', marginTop: 10 }}>
          수신기 로딩 중...
        </div>
      ) : receiversQ.error ? (
        <div style={{ fontSize: 13, color: 'var(--danger)', marginTop: 10 }}>
          수신기 조회 실패
        </div>
      ) : null}
    </Card>
  );
}
